import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ProcessedTicketData } from '../../types/processedTicket';

interface PayoutDialogProps {
  open: boolean;
  payoutType: 'lotto' | 'scratchers';
  selectedPack: ProcessedTicketData | null;
  payoutAmount: string;
  posAmount: string;
  loading: boolean;
  onPayoutAmountChange: (value: string) => void;
  onPosAmountChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const PayoutDialog: React.FC<PayoutDialogProps> = ({
  open,
  payoutType,
  selectedPack,
  payoutAmount,
  posAmount,
  loading,
  onPayoutAmountChange,
  onPosAmountChange,
  onSubmit,
  onCancel,
}) => {
  const isValid = payoutAmount && parseFloat(payoutAmount) > 0 && posAmount && parseFloat(posAmount) > 0;
  const formattedGameNumber = selectedPack?.game_number ? selectedPack.game_number.toString().padStart(4, '0') : '';

  return (
    <Dialog 
      open={open} 
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ background: '#EEE8FF', color: '#222B45', fontWeight: 'bold' }}>
        🎯 {payoutType === 'lotto' ? 'Lotto' : 'Scratchers'} Payout
      </DialogTitle>
      <DialogContent>
        {selectedPack && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Ticket Details
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Game:</strong> {selectedPack.game_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Game Number:</strong> {formattedGameNumber || selectedPack.game_number}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Pack ID:</strong> {selectedPack.pack_id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Ticket Type:</strong> {selectedPack.pack_type?.toUpperCase() || payoutType.toUpperCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Original Price:</strong> ${selectedPack.price_point?.toFixed(2) || '0.00'}
              </Typography>
            </Box>

            <TextField
              autoFocus
              margin="dense"
              label="Payout Amount ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={payoutAmount}
              onChange={(e) => onPayoutAmountChange(e.target.value)}
              inputProps={{ 
                min: "0", 
                step: "0.01",
                placeholder: "Enter payout amount"
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="dense"
              label="POS Amount ($)"
              type="number"
              fullWidth
              variant="outlined"
              value={posAmount}
              onChange={(e) => onPosAmountChange(e.target.value)}
              inputProps={{ 
                min: "0", 
                step: "0.01",
                placeholder: "Enter POS amount"
              }}
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Enter the payout amount for this {payoutType === 'lotto' ? 'lotto' : 'scratchers'} ticket.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={onSubmit} 
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{ color: '#ffffff', fontWeight: 600, fontSize: '15px', backgroundColor: '#2C6CA0' }}
        >
          {loading ? 'Processing...' : 'Process Payout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 
